import { useEffect, useState } from 'react';
import { ImageOff } from 'lucide-react';

interface ProductThumbProps {
  imageLink: string | null;
  /** Kantenlaenge als Tailwind-Klassenpaar, z.B. `h-10 w-10`. */
  groesse: string;
  /** Groesse des Platzhalter-Symbols, z.B. `h-4 w-4`. */
  symbolGroesse: string;
  /**
   * Praefix der Testkennung: `rail` -> `rail-no-image`, `list` ->
   * `list-no-image`. Die beiden Flaechen haben eigene Suiten, und eine
   * gemeinsame Kennung machte deren Zusicherungen ununterscheidbar.
   */
  testIdPraefix: string;
  className?: string;
}

/**
 * Das kleine Vorschaubild der Werkbank — Produktleiste und Listenansicht.
 *
 * EINE Quelle fuer drei Dinge, die vorher zweimal dastanden und beim dritten
 * Mal fehlten:
 *
 *   1. Der BILDTELLER. Das Foto sass frueher nackt auf einer Flaeche, die mit
 *      dem Theme kippt. Produktfotografie kippt nicht: sie ist auf Weiss
 *      aufgenommen oder liegt als PNG mit transparentem Hintergrund vor, und
 *      im Dunkelmodus liess so ein PNG die dunkle Karte durchscheinen — ein
 *      dunkles Produkt wurde unbeurteilbar. Herleitung und Messwerte stehen an
 *      `card/ShoppingTile.tsx`, der grossen Schwester dieser Datei.
 *   2. Der PLATZHALTER bei fehlendem Bild.
 *   3. Der PLATZHALTER bei einem LADEFEHLER. Ein nacktes `<img>` ohne
 *      `onError` zeigt das Browser-Symbol fuer ein kaputtes Bild, und wer es
 *      per `style.display = 'none'` versteckt, laesst die Zeile so aussehen,
 *      als haette das Produkt nie ein Bild gehabt. beutel-shop.ch sitzt hinter
 *      Cloudflare, das Hotlinks mit 403 beantwortet — fuer den Pruefer ein
 *      Fehler der Werkbank, der keiner ist.
 *
 * Die Kachel in `card/ShoppingTile.tsx` teilt diese Datei bewusst NICHT: sie
 * ist zehnmal so gross, fuellt mit `object-contain` statt `object-cover` und
 * beschriftet ihren Platzhalter im Klartext ("nicht hinterlegt" gegen "nicht
 * abrufbar"). Bei 30 px waere dieser Text unlesbar, und eine Komponente mit
 * einem Schalter fuer jeden dieser Unterschiede erklaerte weniger als zwei
 * ehrliche Fassungen.
 *
 * `broken` haengt an `imageLink`, und der Grund dafuer ist NICHT die
 * Virtualisierung: beide Listen keyen ihre Zeilen mit `product.id`, React
 * montiert beim Produktwechsel also ohnehin neu. Der Reset deckt den anderen
 * Fall ab — DASSELBE Produkt bekommt eine neue Bild-URL, etwa nachdem ein Sync
 * sie korrigiert hat. Ohne ihn klebte der Platzhalter am neuen Bild fest.
 * (Die frueher hier stehende Virtualisierungs-Begruendung stand auch im Test,
 * und der hielt stand, als der Reset probeweise entfernt wurde: er beruehrte
 * seinen Gegenstand gar nicht.)
 */
export function ProductThumb({
  imageLink,
  groesse,
  symbolGroesse,
  testIdPraefix,
  className,
}: ProductThumbProps) {
  const [broken, setBroken] = useState(false);
  useEffect(() => {
    setBroken(false);
  }, [imageLink]);

  const gemeinsam = `${groesse} flex-shrink-0 rounded ${className ?? ''}`;

  if (imageLink && !broken) {
    return (
      <img
        src={imageLink}
        alt=""
        className={`${gemeinsam} bg-product-plate object-cover dark:ring-1 dark:ring-inset dark:ring-border`}
        loading="lazy"
        onError={() => setBroken(true)}
      />
    );
  }

  return (
    <span
      data-testid={`${testIdPraefix}-no-image`}
      data-reason={imageLink ? 'blocked' : 'missing'}
      className={`${gemeinsam} flex items-center justify-center bg-muted`}
    >
      <ImageOff className={`${symbolGroesse} text-muted-foreground`} aria-hidden="true" />
    </span>
  );
}
